import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import {
	SolutionCenterContentTypes,
	ZendeskArticle,
	ZendeskCategory,
	ZendeskSection,
} from '../../../types/solution-center';
import { GetZendeskArticlesBySectionId } from '../../../connection/zendesk';
import { setSolutionCenterContentState } from '../../../stores/actions/user-interface';
import LoadingSpinner, { LoadingSpinnerType } from '../../general-components/loading-spinner';

const ARTICLE_LIMT = 6;

interface CategoryViewProps {
	currentCategory: ZendeskCategory;
	sections: ZendeskSection[];
	handleSelectSection: (sectionId: number) => void;
}

interface ArticleMapValue {
	articles: ZendeskArticle[];
	totalArticleCount: number;
}

export default function CategoryView(props: CategoryViewProps) {
	const dispatch = useDispatch();

	const {
		currentCategory,
		sections,
		handleSelectSection,
	} = props;

	const [secArticleMap, setSecArticleMap] = useState<Map<number, ArticleMapValue>>(new Map());
	const [isLoading, setIsLoading] = useState<boolean>(true);

	const currentCategorySections = useMemo(
		() => sections.filter((section) => section.category_id === currentCategory.id),
		[sections, currentCategory],
	);

	const getSectionArticles = async () => {
		setIsLoading(true);
		const newSectionArticleMap = new Map<number, ArticleMapValue>();
		await Promise.all(
			currentCategorySections.map(
				(section) => GetZendeskArticlesBySectionId(section.id, ARTICLE_LIMT).then((res) => {
					if (res.articles) {
						const articleMapValue: ArticleMapValue = {
							articles: res.articles,
							totalArticleCount: res.count,
						};
						newSectionArticleMap.set(section.id, articleMapValue);
					}
				}),
			),
		);
		setSecArticleMap(newSectionArticleMap);
		setIsLoading(false);
	};

	const renderSectionArticles = (sectionId: number) => {
		const sectionArticles = secArticleMap.get(sectionId);
		if (sectionArticles) {
			const showSectionLink = sectionArticles.totalArticleCount > ARTICLE_LIMT;
			return (
				<>
					<ul className={classNames('sc-section-list', showSectionLink && 'show-section-link')}>
						{sectionArticles.articles.map((article) => (
							<li
								key={`article-${article.id}`}
								className="sc-section-list-item"
								onClick={() => dispatch(setSolutionCenterContentState({
									id: article.id,
									type: SolutionCenterContentTypes.Article,
								}))}
							>
								{article.title}
							</li>
						))}
					</ul>
					{showSectionLink && (
						<span className="sc-section-expand-text" onClick={() => handleSelectSection(sectionId)}>
							{`View all ${sectionArticles.totalArticleCount} articles`}
						</span>
					)}
				</>
			);
		}
		return <></>;
	};

	useEffect(() => {
		getSectionArticles();
	}, [currentCategorySections]);

	return (
		isLoading ? (
			<div className="sc-loading">
				<LoadingSpinner
					height={36}
					width={36}
					type={LoadingSpinnerType.Gradient}
					style={{ background: '#26242B' }}
				/>
			</div>
		) : (
			<>
				<div>
					{currentCategorySections.map((section) => (
						<div key={`section-${section.id}`} className="sc-section-container">
							<span
								className="sc-section-header can-click"
								onClick={() => handleSelectSection(section.id)}
							>
								{section.name}
							</span>
							<span className="sc-section-header">&nbsp;&gt;</span>
							{renderSectionArticles(section.id)}
						</div>
					))}
				</div>
				<div className="sc-shadow" />
			</>
		)
	);
}
